import { collection, getDocs, limit, orderBy, query, Timestamp } from 'firebase/firestore'
import FirebaseService from '@/services/FirebaseService'

export const INVOICE_STATUS = {
  OUTSTANDING: 'outstanding',
  OVERDUE: 'overdue',
  PAID: 'paid'
}

export interface SyncMetadata {
  qboId?: string
  lastSynced?: number
  lastSyncStatus?: 'success' | 'error' | 'conflict'
  lastSyncError?: string
  conflictData?: any
  syncToken?: string
}

export class InvoicePayment {
  id: string
  date: Date
  method: string
  amount: number
  memo: string

  constructor() {
    this.date = new Date()
    this.method = ''
    this.amount = 0
    this.memo = ''
  }
}

export class InvoiceItem {
  id: string
  title: string
  quantity: number
  unitPrice: number
  total: number

  constructor() {
    this.id = ''
    this.title = ''
    this.quantity = 1
    this.unitPrice = 0
    this.total = 0
  }
}

/**
 * Invoice class
 * Invoices are payment request to customers
 */
export class Invoice {
  id: string
  status: string
  dueDate: Date
  invoiceDate: Date
  credit: number
  balance: number
  total: number
  invoiceId: string
  projectId: string
  customerId: string
  items: InvoiceItem[]
  attachments: []
  payments: InvoicePayment[]
  syncMeta?: SyncMetadata
  createdAt: Date
  updatedAt: Date

  constructor () {
    this.status = INVOICE_STATUS.OUTSTANDING
    this.credit = 0
    this.balance = 0
    this.total = 0
    this.items = []
    this.attachments = []
    this.invoiceId = ''
    this.payments = []
    this.createdAt = new Date()
    this.updatedAt = new Date()
  }
}

const getLastInvoiceId = async () => {
  const db = await FirebaseService.firestore()
  const collectionRef = collection(db, 'invoices')
  const queryRef = query(collectionRef, orderBy('invoiceId', 'desc'), limit(1))
  const querySnapshot = await getDocs(queryRef)

  if (querySnapshot.empty) return '1000'

  const invoice = Object.assign({}, querySnapshot.docs[0].data())
  return invoice.invoiceId ? invoice.invoiceId.substring(5) : '1000'
}
export const generateInvoiceId = async () => {
  const lastInvoiceId = await getLastInvoiceId()
  const label = 'INV'
  const year = new Date().getFullYear().toString().substring(2)
  const counter = parseInt(lastInvoiceId)
  return `${label}${year}${counter + 1}`
}

export const invoiceConverter = {
  toFirestore: (invoice: Invoice) => {
    invoice.createdAt = invoice.createdAt ? new Date(invoice.createdAt) : new Date()
    invoice.updatedAt = new Date()
    invoice.dueDate = new Date(invoice.dueDate)
    invoice.invoiceDate = new Date(invoice.invoiceDate)
    return Object.assign({}, JSON.parse(JSON.stringify(invoice)))
  },

  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options)
    data.dueDate = (data.dueDate instanceof Timestamp) ? data.dueDate.toDate() : data.dueDate
    data.invoiceDate = (data.invoiceDate instanceof Timestamp) ? data.invoiceDate.toDate() : data.invoiceDate
    data.createdAt = (data.createdAt instanceof Timestamp) ? data.createdAt.toDate() : data.createdAt
    data.updatedAt = (data.updatedAt instanceof Timestamp) ? data.updatedAt.toDate() : data.updatedAt

    return Object.assign(new Invoice(), data) as Invoice
  }
}

export const invoicePaymentConverter = {
  toFirestore: (payment: InvoicePayment) => {
    return Object.assign({}, JSON.parse(JSON.stringify(payment)))
  },

  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options)
    data.date = (data.date instanceof Timestamp) ? data.date.toDate() : data.date

    return Object.assign(new InvoicePayment(), data) as InvoicePayment
  }
}
